import {
  CategoryDto,
  CategoryOptionDto,
  UpdateCategoryOptionDto,
  OptionType,
  Offer,
  OfferWithAnalytics,
  OfferStatus,
  PricingStrategiesResponse,
  DiscountOverrides,
  DiscountPreview,
  // OfferSectionTimeAnalyticsResponse, // Currently unused
  // SessionSectionTime, // Currently unused
} from "../types";

import apiClient from "./apiClient";

export interface CreateOfferPayload {
  title: string;
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  description?: string;
  checkInDate: string;
  checkOutDate: string;
  numberOfRooms: number;
  numberOfGuests: number;
}

export interface UpdateOfferPayload {
  title?: string;
  clientName?: string;
  clientEmail?: string;
  clientPhone?: string;
  description?: string;
  checkInDate?: string;
  checkOutDate?: string;
  numberOfRooms?: number;
  numberOfGuests?: number;
}

// Category operations
export const getCategories = async () => {
  const { data } = await apiClient.get(`/categories`);
  return data;
};

export const createCategory = async (category: Partial<CategoryDto>) => {
  const { data } = await apiClient.post("/categories", category);
  return data;
};

export const updateCategory = async (id: string, category: CategoryDto) => {
  const { data } = await apiClient.patch(`/categories/${id}`, category);
  return data;
};

// NEW: Category Options by type operations
export const getCategoryOptionsByType = async (
  type?: OptionType,
  includeInactive?: boolean
) => {
  const params = new URLSearchParams();
  if (type) params.append("type", type);
  if (includeInactive) params.append("includeInactive", "true");
  const queryString = params.toString();
  const { data } = await apiClient.get(
    `/categories/options${queryString ? `?${queryString}` : ""}`
  );
  return data;
};

// NEW: Category Option activation/deactivation
export const activateComponent = async (id: string) => {
  const { data } = await apiClient.patch(`/categories/${id}/activate`);
  return data;
};

export const deactivateComponent = async (id: string) => {
  const { data } = await apiClient.patch(`/categories/${id}/deactivate`);
  return data;
};

// Component (CategoryOption) operations
export const createComponent = async (
  component: Omit<
    CategoryOptionDto,
    "id" | "createdAt" | "updatedAt" | "hotelId"
  >
) => {
  const { data } = await apiClient.post(`/categories/options`, component);
  return data;
};

export const updateComponent = async (
  id: string,
  component: UpdateCategoryOptionDto
) => {
  const { data } = await apiClient.patch(
    `/categories/options/${id}`,
    component
  );
  return data;
};

export const reorderComponents = async (
  categoryId: string,
  reorderedItems: { id: string; sortOrder: number }[]
) => {
  const { data } = await apiClient.post(
    `/categories/${categoryId}/options/reorder`,
    reorderedItems
  );
  return data;
};

export const createOffer = async (offerData: CreateOfferPayload) => {
  const { data } = await apiClient.post("/offers", offerData);
  return data;
};

export const updateOffer = async (
  offerId: string,
  offerData: UpdateOfferPayload
) => {
  const { data } = await apiClient.put(`/offers/${offerId}`, offerData);
  return data;
};

export const getOffers = async (): Promise<OfferWithAnalytics[]> => {
  const { data } = await apiClient.get("/offers/with-analytics");
  return data;
};

// NEW: Analytics-enabled offers list
export const getOffersWithAnalytics = async () => {
  const { data } = await apiClient.get("/offers/with-analytics");
  return data;
};

// NEW: Individual offer analytics
export const getOfferAnalyticsSummary = async (offerId: string) => {
  const { data } = await apiClient.get(`/offers/${offerId}/analytics/summary`);
  return data;
};

export const getOfferAnalyticsSessions = async (offerId: string) => {
  const { data } = await apiClient.get(`/offers/${offerId}/analytics/sessions`);
  return data;
};

// Authentication operations
export const loginWithPassword = async (email: string, password: string) => {
  const { data } = await apiClient.post("/auth/login-password", {
    email,
    password,
  });
  return data;
};

export const setPassword = async (password: string) => {
  const { data } = await apiClient.post("/auth/set-password", { password });
  return data;
};

export const changePassword = async (
  oldPassword: string,
  newPassword: string
) => {
  const { data } = await apiClient.post("/auth/change-password", {
    oldPassword,
    newPassword,
  });
  return data;
};

export const getCurrentUser = async () => {
  const { data } = await apiClient.get("/auth/me");
  return data;
};

export const updateProfile = async (firstName: string, lastName: string) => {
  const { data } = await apiClient.put("/auth/profile", {
    firstName,
    lastName,
  });
  return data;
};

// Redirect token operations
export const createRedirectToken = async (): Promise<{
  alreadyOnCorrectDomain?: boolean;
  redirectToken?: string;
  redirectUrl?: string;
  expiresAt?: string;
  tenant: {
    id: string;
    subdomain: string;
    name: string;
  };
}> => {
  const { data } = await apiClient.post("/auth/create-redirect-token");
  return data;
};

export const exchangeRedirectToken = async (
  redirectToken: string
): Promise<{ access_token: string }> => {
  const { data } = await apiClient.post("/auth/exchange-redirect-token", {
    redirectToken,
  });
  return data;
};

// User management operations
export const getHotelUsers = async () => {
  const { data } = await apiClient.get("/users");
  return data;
};

export const createUser = async (userData: {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}) => {
  const { data } = await apiClient.post("/users", userData);
  return data;
};

export const updateUser = async (
  userId: string,
  userData: {
    email?: string;
    firstName?: string;
    lastName?: string;
    role?: string;
    isActive?: boolean;
  }
) => {
  const { data } = await apiClient.put(`/users/${userId}`, userData);
  return data;
};

export const deleteUser = async (userId: string) => {
  const { data } = await apiClient.delete(`/users/${userId}`);
  return data;
};

// New wizard API functions for Phase 3b
export const generateOfferDays = async (offerId: string) => {
  const { data } = await apiClient.post(`/offers/${offerId}/generate-days`);
  return data;
};

export const addDayPlanItem = async (
  offerId: string,
  dayId: string,
  itemData: {
    categoryOptionId?: string;
    unitPrice?: number;
    quantity?: number;
    occupancyType?: string;
    notes?: string | null;
    timeFrom?: string;
    timeTo?: string;
    customTitle?: string;
    vatRate?: number;
    [key: string]: any;
  }
) => {
  const { data } = await apiClient.post(
    `/offers/${offerId}/days/${dayId}/items`,
    itemData
  );
  return data;
};

export const updateDayPlanItem = async (
  offerId: string,
  dayId: string,
  itemId: string,
  itemData: any
) => {
  const { data } = await apiClient.put(
    `/offers/${offerId}/days/${dayId}/items/${itemId}`,
    itemData
  );
  return data;
};

export const addBulkDayPlanItems = async (
  offerId: string,
  dayId: string,
  bulkData: {
    categoryOptionIds: string[];
    defaultQuantity?: number;
    timeFrom?: string;
    timeTo?: string;
  }
) => {
  const { data } = await apiClient.post(
    `/offers/${offerId}/days/${dayId}/items/bulk`,
    bulkData
  );
  return data;
};

export const deleteDayPlanItem = async (
  offerId: string,
  dayId: string,
  itemId: string
) => {
  const { data } = await apiClient.delete(
    `/offers/${offerId}/days/${dayId}/items/${itemId}`
  );
  return data;
};

export const getDaySummary = async (offerId: string, dayId: string) => {
  const { data } = await apiClient.get(
    `/offers/${offerId}/days/${dayId}/summary`
  );
  return data;
};

export const getOfferDetails = async (offerId: string) => {
  const { data } = await apiClient.get(`/offers/${offerId}`);
  return data;
};

export const updateOfferDates = async (
  offerId: string,
  checkInDate: string,
  checkOutDate: string
) => {
  const { data } = await apiClient.put(`/offers/${offerId}/update-dates`, {
    checkInDate,
    checkOutDate,
  });
  return data;
};

export const previewOfferDateChange = async (
  offerId: string,
  checkInDate: string,
  checkOutDate: string
) => {
  const { data } = await apiClient.get(
    `/offers/${offerId}/preview-date-change`,
    { params: { checkInDate, checkOutDate } }
  );
  return data;
};

export const reorderDayPlanItems = async (
  offerId: string,
  dayId: string,
  reorderData: { id: string; sortOrder: number }[]
) => {
  const { data } = await apiClient.post(
    `/offers/${offerId}/days/${dayId}/items/reorder`,
    reorderData
  );
  return data;
};

export const createAccommodationFullStay = async (
  offerId: string,
  dayId: string,
  accommodationData: {
    categoryOptionId: string;
    unitPrice: number;
    quantity: number;
    occupancyType?: string;
    notes?: string | null;
    timeFrom?: string;
    timeTo?: string;
    dailyPrices?: { [dayId: string]: number };
    /** Calculated price per night (saved as suggestedPrice). */
    dailySuggestedPrices?: { [dayId: string]: number };
    /** Net base prices per night (saved as profitroomPrice*). */
    dailyBasePrices?: {
      [dayId: string]: { single?: number; double?: number; triple?: number };
    };
    personsCount?: number;
    vatRate?: number;
  }
) => {
  const { data } = await apiClient.post(
    `/offers/${offerId}/days/${dayId}/items/accommodation-full-stay`,
    accommodationData
  );
  return data;
};

export const unlinkItem = async (
  offerId: string,
  dayId: string,
  itemId: string
) => {
  const { data } = await apiClient.put(
    `/offers/${offerId}/days/${dayId}/items/${itemId}/unlink`
  );
  return data;
};

export const unlinkAllChildren = async (
  offerId: string,
  dayId: string,
  itemId: string
) => {
  const { data } = await apiClient.put(
    `/offers/${offerId}/days/${dayId}/items/${itemId}/unlink-all`
  );
  return data;
};

// Offer publication
export const publishOffer = async (offerId: string) => {
  const { data } = await apiClient.post(`/offers/${offerId}/publish`);
  return data;
};

export const duplicateOffer = async (offerId: string): Promise<Offer> => {
  const { data } = await apiClient.post(`/offers/${offerId}/duplicate`);
  return data;
};

export const deleteOffer = async (offerId: string): Promise<void> => {
  await apiClient.delete(`/offers/${offerId}`);
};

// Offer Versioning
export interface OfferVersion {
  id: string;
  publicToken: string;
  /**
   * ISO date string from JSON (Backend `Date` is serialized as string).
   * Convert with `new Date(publishedAt)` before use.
   */
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  status: OfferStatus;
  title: string;
  /**
   * Net value as string — TypeORM `decimal(10,2)` is returned as string by the pg driver.
   * Convert with `Number(netValue)` before numeric/formatting use.
   */
  netValue?: string;
  lineItems?: { id: string }[];
  itemCount?: number;
  /**
   * 1-based version number assigned by backend (newest = highest).
   * Preferred over client-side index-based numbering. Optional for
   * backward compatibility during rollout.
   */
  versionNumber?: number;
  /**
   * Number of line items in this version, computed by backend.
   * Preferred over `lineItems.length` and `itemCount` (legacy). Optional
   * for backward compatibility during rollout.
   */
  lineItemsCount?: number;
}

export const getOfferVersions = async (
  offerId: string
): Promise<OfferVersion[]> => {
  const { data } = await apiClient.get(`/offers/${offerId}/versions`);
  return data;
};

export const createOfferVersion = async (
  offerId: string
): Promise<{ id: string; message: string; isDraft: boolean }> => {
  const { data } = await apiClient.post(`/offers/${offerId}/create-version`);
  return data;
};

export const publishVersion = async (
  draftId: string
): Promise<{
  id: string;
  message: string;
  publishedAt: Date;
  mainOfferPublicToken: string;
}> => {
  const { data } = await apiClient.post(`/offers/${draftId}/publish-version`);
  return data;
};

export const rejectDraft = async (
  draftId: string
): Promise<{ message: string }> => {
  const { data } = await apiClient.delete(`/offers/${draftId}/reject-draft`);
  return data;
};

// Public offer access
export const getPublicOffer = async (publicToken: string) => {
  const { data } = await apiClient.get(`/offers/public/${publicToken}`);
  return data;
};

export const getPublicOfferVersion = async (
  publicToken: string,
  versionToken: string
) => {
  const { data } = await apiClient.get(
    `/offers/public/${publicToken}/v/${versionToken}`
  );
  return data;
};

export const getPublicOfferVersions = async (
  publicToken: string
): Promise<OfferVersion[]> => {
  const { data } = await apiClient.get(
    `/offers/public/${publicToken}/versions`
  );
  return data;
};

// Profitroom integration
export const getProfitRoomAvailability = async (params: {
  checkIn: string;
  checkOut: string;
  single: number;
  pairs: number;
}) => {
  const { data } = await apiClient.get(`/accommodations/availability`, {
    params,
  });
  return data;
};

// NEW: Per-day availability for single day
export const getDayAvailability = async (offerId: string, dayId: string) => {
  const { data } = await apiClient.get(
    `/accommodations/day-availability/${offerId}/${dayId}`
  );
  return data;
};

// NEW: Batch availability for all offer days
export const getBatchAvailability = async (offerId: string) => {
  const { data } = await apiClient.get(
    `/accommodations/batch-availability/${offerId}`
  );
  return data;
};

// Day accommodation management
export const updateDayAccommodation = async (
  offerId: string,
  dayId: string,
  accommodationData: { pairs: number; singles: number; singlesAlone: number }
) => {
  const { data } = await apiClient.patch(
    `/offers/${offerId}/days/${dayId}/accommodation`,
    accommodationData
  );
  return data;
};

// Section Time Analytics
export const getOfferSectionTimeAnalytics = async (offerId: string) => {
  const { data } = await apiClient.get(
    `/offers/${offerId}/analytics/section-time`
  );
  return data;
};

export const getSessionSectionTimeDetails = async (
  offerId: string,
  sessionId: string
) => {
  const { data } = await apiClient.get(
    `/offers/${offerId}/analytics/sessions/${sessionId}/section-time`
  );
  return data;
};

// Brand Kit API functions
export const getBrandKit = async (hotelId: string) => {
  const { data } = await apiClient.get(`/hotels/${hotelId}/brand-kit`);
  return data;
};

export const updateBrandKit = async (hotelId: string, brandKitData: any) => {
  const { data } = await apiClient.patch(
    `/hotels/${hotelId}/brand-kit`,
    brandKitData
  );
  return data;
};

export const uploadHeroImage = async (hotelId: string, file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await apiClient.post(
    `/hotels/${hotelId}/brand-kit/upload-hero`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return data;
};

// Financial Summary API
export const getFinancialSummary = async (offerId: string) => {
  const { data } = await apiClient.get(`/offers/${offerId}/financial-summary`);
  return data;
};

// Accommodation Markup API
export const updateAccommodationMarkup = async (
  offerId: string,
  markupData: {
    accommodationMarkupType: "percentage" | "amount";
    accommodationMarkupValue: number;
    accommodationWeekendSurchargePercent?: number;
  }
) => {
  const { data } = await apiClient.patch(
    `/offers/${offerId}/markup`,
    markupData
  );
  return data;
};

// Discount Overrides API
export const getDiscountPreview = async (
  offerId: string
): Promise<DiscountPreview> => {
  const { data } = await apiClient.get(`/offers/${offerId}/discount-preview`);
  return data;
};

export const updateDiscountOverrides = async (
  offerId: string,
  overrides: Omit<DiscountOverrides, "customDiscounts"> & {
    customDiscounts?: Array<{ name: string; percentage: number }>;
  }
): Promise<Offer> => {
  const { data } = await apiClient.patch(
    `/offers/${offerId}/discounts`,
    overrides
  );
  return data;
};

export const resetDiscountOverrides = async (
  offerId: string
): Promise<Offer> => {
  const { data } = await apiClient.delete(`/offers/${offerId}/discounts`);
  return data;
};

// Files API
export const getFiles = async (
  filters: {
    includeArchived?: boolean;
    category?: string;
    fileType?: string;
  } = {}
) => {
  const params = new URLSearchParams();
  if (filters.includeArchived) params.append("includeArchived", "true");
  if (filters.category) params.append("category", filters.category);
  if (filters.fileType) params.append("fileType", filters.fileType);
  const queryString = params.toString();

  const { data } = await apiClient.get(
    `/files${queryString ? `?${queryString}` : ""}`
  );
  return data;
};

export const uploadFile = async (
  file: File,
  metadata: {
    displayName: string;
    description?: string;
    category?: string;
    defaultNote?: string;
    tags?: string[];
  }
) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("displayName", metadata.displayName);
  if (metadata.description)
    formData.append("description", metadata.description);
  if (metadata.category) formData.append("category", metadata.category);
  if (metadata.defaultNote)
    formData.append("defaultNote", metadata.defaultNote);
  if (metadata.tags && metadata.tags.length > 0) {
    formData.append("tags", JSON.stringify(metadata.tags));
  }

  const { data } = await apiClient.post(`/files`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return data;
};

export const updateFile = async (
  fileId: string,
  updateData: {
    displayName?: string;
    description?: string;
    category?: string;
    defaultNote?: string;
    tags?: string[];
  }
) => {
  const { data } = await apiClient.patch(`/files/${fileId}`, updateData);
  return data;
};

export const archiveFile = async (fileId: string) => {
  const { data } = await apiClient.delete(`/files/${fileId}`);
  return data;
};

export const restoreFile = async (fileId: string) => {
  const { data } = await apiClient.post(`/files/${fileId}/restore`);
  return data;
};

// Pricing Strategies API
export const getPricingStrategies = async (
  checkIn: string,
  checkOut: string
): Promise<PricingStrategiesResponse> => {
  const { data } = await apiClient.get(`/accommodations/pricing-strategies`, {
    params: {
      checkIn,
      checkOut,
    },
  });
  return data;
};

// Offer Files API
export const getOfferFiles = async (offerId: string) => {
  const { data } = await apiClient.get(`/offers/${offerId}/files`);
  return data;
};

export const getAvailableFilesForOffer = async (offerId: string) => {
  const { data } = await apiClient.get(`/offers/${offerId}/files/available`);
  return data;
};

export const attachFileToOffer = async (
  offerId: string,
  fileId: string,
  customDescription?: string
) => {
  const { data } = await apiClient.post(`/offers/${offerId}/files`, {
    fileId,
    customDescription,
  });
  return data;
};

export const updateOfferFile = async (
  offerId: string,
  offerFileId: string,
  updateData: {
    customDescription?: string;
    sortOrder?: number;
  }
) => {
  const { data } = await apiClient.put(
    `/offers/${offerId}/files/${offerFileId}`,
    updateData
  );
  return data;
};

export const removeFileFromOffer = async (
  offerId: string,
  offerFileId: string
) => {
  const { data } = await apiClient.delete(
    `/offers/${offerId}/files/${offerFileId}`
  );
  return data;
};

export const reorderOfferFiles = async (offerId: string, fileIds: string[]) => {
  const { data } = await apiClient.put(`/offers/${offerId}/files/reorder`, {
    fileIds,
  });
  return data;
};

// Hotel Settings API
export interface HotelSettingsResponse {
  breakfastNetPricePerPerson: number | null;
  checkInNotes: string | null;
  checkOutNotes: string | null;
}

export const getHotelSettings = async (
  hotelId: string
): Promise<HotelSettingsResponse> => {
  const { data } = await apiClient.get(`/hotels/${hotelId}/settings`);
  return data;
};

export const updateHotelSettings = async (
  hotelId: string,
  settings: {
    breakfastNetPricePerPerson?: number;
    checkInNotes?: string | null;
    checkOutNotes?: string | null;
  }
) => {
  const { data } = await apiClient.patch(
    `/hotels/${hotelId}/settings`,
    settings
  );
  return data;
};

// Optional Items API
export interface CreateOptionalItemData {
  title: string;
  unitPrice: number;
  quantity: number;
  categoryOptionId?: string;
}

export interface UpdateOptionalItemData {
  title?: string;
  unitPrice?: number;
  quantity?: number;
  sortOrder?: number;
}

export interface HotelOfferOptionalItem {
  id: string;
  title: string;
  unitPrice: number;
  quantity: number;
  totalPrice: number;
  sortOrder: number;
  categoryOption?: {
    id: string;
    title: string;
    description?: string;
    optionType: string;
  };
  createdAt: string;
  updatedAt: string;
}

export const getOfferOptionalItems = async (
  offerId: string
): Promise<HotelOfferOptionalItem[]> => {
  const { data } = await apiClient.get(`/offers/${offerId}/optional-items`);
  return data;
};

export const createOptionalItem = async (
  offerId: string,
  itemData: CreateOptionalItemData
): Promise<HotelOfferOptionalItem> => {
  const { data } = await apiClient.post(
    `/offers/${offerId}/optional-items`,
    itemData
  );
  return data;
};

export const updateOptionalItem = async (
  offerId: string,
  itemId: string,
  updateData: UpdateOptionalItemData
): Promise<HotelOfferOptionalItem> => {
  const { data } = await apiClient.put(
    `/offers/${offerId}/optional-items/${itemId}`,
    updateData
  );
  return data;
};

export const deleteOptionalItem = async (
  offerId: string,
  itemId: string
): Promise<void> => {
  await apiClient.delete(`/offers/${offerId}/optional-items/${itemId}`);
};

// Guest Count Propagation API

export interface GuestCountPropagationPreviewItem {
  id: string;
  title: string;
  dayNumber: number;
  dayDate: string | null;
  currentQuantity: number;
}

export interface GuestCountPropagationPreview {
  oldCount: number;
  newCount: number;
  items: GuestCountPropagationPreviewItem[];
  totalItems: number;
  skippedItems: number;
}

export const previewGuestCountPropagation = async (
  offerId: string,
  newCount: number,
  signal?: AbortSignal
): Promise<GuestCountPropagationPreview> => {
  const { data } = await apiClient.post(
    `/offers/${offerId}/guest-count-propagation/preview`,
    { newCount },
    { signal }
  );
  return data;
};

export const applyGuestCountPropagation = async (
  offerId: string,
  newCount: number,
  expectedOldCount?: number,
): Promise<Offer> => {
  const { data } = await apiClient.post(
    `/offers/${offerId}/guest-count-propagation/apply`,
    { newCount, expectedOldCount },
  );
  return data;
};

export default apiClient;
